import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button, Grid, TextField, Paper } from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import EditIcon from "@material-ui/icons/Edit";
import CodeIcon from "@material-ui/icons/Code";
import { Alert } from "@material-ui/lab";
import { LoadingSpinner } from "../../../utils/LoadingSpinner";
import { ValidationUtils } from "../utils/ValidationUtils";
import PatientDetailsForm from "../components/PatientDetailsForm";
import PlaceOfServiceSelector from "../components/PlaceOfServiceSelector";
import PrescriberSelector from "../components/PrescriberSelector";
import MedicationSelector from "../components/MedicationSelector";
import PharmacyDetails from "../components/PharmacyDetails";

import { generateMessageId } from "../utils/PrescriptionHelpers";
import {
  useUPMSIngestion,
  usePrescriptionForm,
  useTransferRxForm,
  useTransferRxDefaults,
} from "../hooks";
import { PREDEFINED_ADDRESSES } from "../config/addresses";
import { COLORS } from "../styles";
import {
  WorkflowCard,
  WorkflowSelectionHeader,
  StyledHeaderBox,
  StyledActionButton,
  DataAccordion,
  SourcePharmacyForm,
  TransferDetailsForm,
  CustomerSearchSection,
  CustomerCreationSection,
  ResetWorkflowButton,
} from "../components";

interface IProps {
  data?: any;
}
enum WorkflowType {
  NONE = "NONE",
  MANUAL_ENTRY = "MANUAL_ENTRY",
  UPLOAD_JSON = "UPLOAD_JSON",
}

const IngestTransferRxToUPMSSection: React.FC<IProps> = () => {
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowType>(
    WorkflowType.NONE
  );
  const [jsonInput, setJsonInput] = useState("");
  const [validationError, setValidationError] = useState<string>("");
  const [transferFieldErrors, setTransferFieldErrors] = useState<{
    [key: string]: string;
  }>({});

  const responseRef = useRef<HTMLDivElement>(null);
  const workflowContentRef = useRef<HTMLDivElement>(null);

  // Use TransferRx form hook for all transfer-specific state
  const transferForm = useTransferRxForm();
  const {
    sourcePharmacyNCPDP,
    setSourcePharmacyNCPDP,
    sourcePharmacyNPI,
    setSourcePharmacyNPI,
    sourcePharmacyBusinessName,
    setSourcePharmacyBusinessName,
    sourcePharmacyAddressLine1,
    setSourcePharmacyAddressLine1,
    sourcePharmacyCity,
    setSourcePharmacyCity,
    sourcePharmacyState,
    setSourcePharmacyState,
    sourcePharmacyZipCode,
    setSourcePharmacyZipCode,
    refillsRemaining,
    setRefillsRemaining,
    quantityRemaining,
    setQuantityRemaining,
    quantityTransferred,
    setQuantityTransferred,
    expirationDate,
    setExpirationDate,
    writtenDate,
    setWrittenDate,
    lastFillDate,
    setLastFillDate,
    buildTransferRxData,
  } = transferForm;
  const step2Ref = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const patientFormRef = useRef<HTMLDivElement>(null);

  const {
    isIngesting,
    upmsResponse,
    transformedPayload,
    error: upmsError,
    ingestTransferRx,
    reset: resetUPMS,
  } = useUPMSIngestion();

  const {
    patientDetails,
    prescriberDetails,
    medicationDetails,
    selectedPrescriber,
    selectedMedication,
    placeOfService,
    fieldErrors,
    setPatientField,
    handlePrescriberChange,
    handleMedicationChange,
    handleMedicationFieldChange,
    handlePlaceOfServiceChange,
    resetForm,
  } = usePrescriptionForm();

  const error = upmsError || validationError;

  const syncMedicationWrittenDate = useCallback(
    (date: string) => handleMedicationFieldChange("writtenDate", date),
    [handleMedicationFieldChange]
  );
  useTransferRxDefaults(transferForm, medicationDetails, syncMedicationWrittenDate);

  // Scroll to error when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      setTimeout(() => {
        errorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [error]);

  const handleBackToWorkflowSelection = () => {
    // Reset all state
    resetUPMS();
    resetForm();
    setJsonInput("");
    setValidationError("");
    setTransferFieldErrors({});
    setSelectedWorkflow(WorkflowType.NONE);
  };

  const handleSelectAddress = (address: any) => {
    setPatientField("addressLine1", address.addressLine1);
    setPatientField("addressLine2", address.addressLine2);
    setPatientField("city", address.city);
    setPatientField("state", address.stateName);
    setPatientField("zipCode", address.zipCode);
  };

  const handleWorkflowSelection = (workflow: WorkflowType) => {
    setSelectedWorkflow(workflow);
    // Scroll to workflow content after a short delay to allow rendering
    setTimeout(() => {
      workflowContentRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  // Extract patient details for CustomerCreationSection
  const extractedPatientDetails = patientDetails
    ? {
        firstName: patientDetails.firstName,
        lastName: patientDetails.lastName,
        dateOfBirth: patientDetails.dateOfBirth,
        gender: patientDetails.gender,
        phoneNumber: patientDetails.phoneNumber,
        zipCode: patientDetails.zipCode,
      }
    : undefined;

  const handleSubmit = async () => {
    if (selectedWorkflow === WorkflowType.UPLOAD_JSON) {
      if (!jsonInput.trim()) return;
      try {
        const parsedJson = JSON.parse(jsonInput);
        await ingestTransferRx(
          parsedJson,
          `transfer_${generateMessageId()}.json`
        );
        setTimeout(
          () =>
            responseRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            }),
          100
        );
      } catch (err) {
        // Error is already handled by the hook
      }
    } else {
      // Validate patient details
      const patientValidation =
        ValidationUtils.validatePatientDetails(patientDetails);
      
      // Validate medication details
      const medicationValidation = ValidationUtils.validateMedicationDetails({
        daysSupply: medicationDetails.daysSupply,
        ndc: medicationDetails.ndc,
        sigText: medicationDetails.sigText,
        quantity: medicationDetails.quantity,
        writtenDate: medicationDetails.writtenDate,
      });

      // Validate source pharmacy
      const pharmacyValidation = ValidationUtils.validateSourcePharmacy({
        ncpdp: sourcePharmacyNCPDP,
        npi: sourcePharmacyNPI,
        businessName: sourcePharmacyBusinessName,
        addressLine1: sourcePharmacyAddressLine1,
        city: sourcePharmacyCity,
        state: sourcePharmacyState,
        zipCode: sourcePharmacyZipCode,
      });

      // Validate transfer details
      const transferValidation = ValidationUtils.validateTransferDetails({
        refillsRemaining,
        quantityRemaining,
        quantityTransferred,
        lastFillDate,
        writtenDate,
        expirationDate,
        medicationRefills: medicationDetails.numberOfRefills
          ? parseInt(medicationDetails.numberOfRefills)
          : undefined,
      });

      // Combine all field errors (filter out undefined values)
      const allFieldErrors: { [key: string]: string } = {};
      Object.entries({
        ...patientValidation.fieldErrors,
        ...medicationValidation.fieldErrors,
        ...pharmacyValidation.fieldErrors,
        ...transferValidation.fieldErrors,
      }).forEach(([key, value]) => {
        if (value) allFieldErrors[key] = value;
      });

      setTransferFieldErrors(allFieldErrors);

      // Check if any validation failed
      if (
        !patientValidation.isValid ||
        !medicationValidation.isValid ||
        !pharmacyValidation.isValid ||
        !transferValidation.isValid
      ) {
        const allErrors = [
          ...patientValidation.errors,
          ...medicationValidation.errors,
          ...pharmacyValidation.errors,
          ...transferValidation.errors,
        ];

        const firstErrorField = Object.keys(allFieldErrors)[0];
        const errorMessages = allErrors.join(", ");

        setValidationError(`Please fix the following fields: ${errorMessages}`);

        // Scroll to the first error field
        setTimeout(() => {
          const firstErrorElement = document.querySelector(
            `[name="${firstErrorField}"]`
          );
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);

        return;
      }

      // Clear validation errors if validation passes
      setValidationError("");
      setTransferFieldErrors({});

      try {
        const data = buildTransferRxData(
          patientDetails,
          prescriberDetails,
          medicationDetails,
          placeOfService
        );
        await ingestTransferRx(data, `transfer_${generateMessageId()}.json`);
        setTimeout(
          () =>
            responseRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            }),
          100
        );
      } catch (err) {
        // Error is already handled by the hook
      }
    }
  };

  return (
    <Grid container direction="column" justifyContent="center" spacing={3}>
      {selectedWorkflow === WorkflowType.NONE && (
        <Grid container spacing={4} style={{ maxWidth: 900, marginTop: 8 }}>
          <Grid item xs={12}>
            <WorkflowSelectionHeader
              title="Choose Your Workflow"
              subtitle="Select how you'd like to ingest a transfer prescription"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <WorkflowCard
              icon={EditIcon}
              title="Manual Form Entry"
              description="• Fill in patient details manually<br />• Choose prescriber, medication & pharmacy<br />• Ingest to UPMS<br />• Optionally create AP customer after (Note: May create duplicate profile)"
              onClick={() => handleWorkflowSelection(WorkflowType.MANUAL_ENTRY)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <WorkflowCard
              icon={CodeIcon}
              title="Upload JSON"
              description="• Paste prescription JSON directly<br />• Full control over payload<br />• Ingest to UPMS<br />• No customer creation option"
              onClick={() => handleWorkflowSelection(WorkflowType.UPLOAD_JSON)}
            />
          </Grid>
        </Grid>
      )}

      {selectedWorkflow === WorkflowType.UPLOAD_JSON && (
        <div ref={workflowContentRef}>
          <Grid item xs={12}>
            <Button
              variant="text"
              onClick={handleBackToWorkflowSelection}
              startIcon={<ArrowBackIcon />}
              style={{ color: "#546e7a", marginBottom: 16 }}
            >
              Back to Workflow Selection
            </Button>
          </Grid>
          {error && (
            <Grid item xs={12} style={{ marginTop: 24, marginBottom: 24 }}>
              <div ref={errorRef}>
                <Alert severity="error" onClose={() => {}}>
                  <strong>Error:</strong> {error}
                </Alert>
              </div>
            </Grid>
          )}

          <StyledHeaderBox
            title="Ingest TransferRx JSON to UPMS"
            subtitle="Direct JSON ingestion to UPMS"
            color="#546e7a"
            icon="1"
            infoMessage="This workflow allows you to paste a TransferRx prescription JSON payload directly and ingest it to UPMS (Pharmacy Unified Prescription Message Service). This is useful for testing custom payloads or when you already have the JSON data. Note: This workflow does not support customer creation - use the manual entry workflow if you need that functionality."
          />

          <Grid item xs={12}>
            <Paper
              elevation={1}
              style={{
                padding: 32,
                borderRadius: 8,
                backgroundColor: COLORS.white,
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    multiline
                    minRows={20}
                    label="TransferRx Prescription JSON"
                    variant="filled"
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                    fullWidth
                    placeholder='{"MessageType": "TransferRx", ...}'
                  />
                </Grid>
                <Grid item xs={12}>
                  <StyledActionButton
                    label="Ingest TransferRx to UPMS"
                    loadingLabel="Ingesting..."
                    onClick={handleSubmit}
                    disabled={isIngesting}
                    loading={isIngesting}
                    color={COLORS.orange}
                    startIcon={<SendIcon />}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          {transformedPayload && (
            <Grid item xs={12}>
              <DataAccordion
                title="Transformed UPMS Payload"
                data={transformedPayload}
              />
            </Grid>
          )}
          {upmsResponse && (
            <Grid item xs={12}>
              <div ref={responseRef}>
                <DataAccordion
                  title="UPMS Ingestion Response"
                  data={upmsResponse}
                />
              </div>
            </Grid>
          )}
        </div>
      )}

      {selectedWorkflow === WorkflowType.MANUAL_ENTRY && (
        <div ref={workflowContentRef}>
          <Grid item xs={12}>
            <Button
              variant="text"
              onClick={handleBackToWorkflowSelection}
              startIcon={<ArrowBackIcon />}
              style={{ color: "black", marginBottom: 16 }}
            >
              Back to Workflow Selection
            </Button>
          </Grid>
          {error && (
            <Grid item xs={12} style={{ marginTop: 24, marginBottom: 24 }}>
              <div ref={errorRef}>
                <Alert severity="error" onClose={() => {}}>
                  <strong>Error:</strong> {error}
                </Alert>
              </div>
            </Grid>
          )}
          <StyledHeaderBox
            title="Ingest TransferRx to UPMS"
            subtitle="Transfer prescriptions from other pharmacies to Amazon Pharmacy"
            color="#546e7a"
            icon="1"
            infoMessage={
              <>
                This tool automates the steps from the{" "}
                <a
                  href="https://w.amazon.com/bin/view/Pharmacy/Public/Teams/APAQ/HowTos/HowtoIngestTransferRxusingUPMS"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: COLORS.primary }}
                >
                  TransferRx Ingestion SOP
                </a>
                . Fill in the patient details, source pharmacy information,
                prescriber, and medication details below. You can optionally
                create an Amazon Pharmacy customer profile after ingesting.
              </>
            }
          />

          <Grid container spacing={3}>
            <CustomerSearchSection
              onCustomerRetrieved={(details) => {
                // Update all patient fields at once
                Object.entries(details).forEach(([field, value]) => {
                  setPatientField(
                    field as keyof typeof patientDetails,
                    value as string
                  );
                });
              }}
              scrollTargetRef={patientFormRef}
            />

            <div ref={patientFormRef}>
              <PatientDetailsForm
                patientDetails={patientDetails}
                onFieldChange={setPatientField}
                fieldErrors={fieldErrors}
                addresses={PREDEFINED_ADDRESSES}
                onAddressSelect={handleSelectAddress}
                showAddressDropdown={true}
              />
            </div>
            <PlaceOfServiceSelector
              placeOfService={placeOfService}
              onPlaceOfServiceChange={handlePlaceOfServiceChange}
            />
            <PrescriberSelector
              selectedPrescriber={selectedPrescriber}
              prescriberDetails={prescriberDetails}
              onPrescriberChange={handlePrescriberChange}
            />
            <MedicationSelector
              selectedMedication={selectedMedication}
              medicationDetails={medicationDetails}
              onMedicationChange={handleMedicationChange}
              onMedicationFieldChange={handleMedicationFieldChange}
              fieldErrors={fieldErrors}
            />
            <PharmacyDetails />

            <SourcePharmacyForm
              ncpdp={sourcePharmacyNCPDP}
              npi={sourcePharmacyNPI}
              businessName={sourcePharmacyBusinessName}
              addressLine1={sourcePharmacyAddressLine1}
              city={sourcePharmacyCity}
              state={sourcePharmacyState}
              zipCode={sourcePharmacyZipCode}
              onNCPDPChange={setSourcePharmacyNCPDP}
              onNPIChange={setSourcePharmacyNPI}
              onBusinessNameChange={setSourcePharmacyBusinessName}
              onAddressLine1Change={setSourcePharmacyAddressLine1}
              onCityChange={setSourcePharmacyCity}
              onStateChange={setSourcePharmacyState}
              onZipCodeChange={setSourcePharmacyZipCode}
              fieldErrors={transferFieldErrors}
              disabled={!!upmsResponse}
            />

            <TransferDetailsForm
              refillsRemaining={refillsRemaining}
              quantityRemaining={quantityRemaining}
              quantityTransferred={quantityTransferred}
              lastFillDate={lastFillDate}
              writtenDate={writtenDate}
              expirationDate={expirationDate}
              onRefillsRemainingChange={setRefillsRemaining}
              onQuantityRemainingChange={setQuantityRemaining}
              onQuantityTransferredChange={setQuantityTransferred}
              onLastFillDateChange={setLastFillDate}
              onWrittenDateChange={setWrittenDate}
              onExpirationDateChange={setExpirationDate}
              fieldErrors={transferFieldErrors}
              disabled={!!upmsResponse}
            />
            <Grid item xs={12} style={{ marginTop: 16 }}>
              <StyledActionButton
                label="Ingest TransferRx to UPMS"
                loadingLabel="Ingesting..."
                onClick={handleSubmit}
                disabled={isIngesting || !!upmsResponse}
                loading={isIngesting}
                color={COLORS.orange}
                startIcon={<SendIcon />}
              />
            </Grid>
            {transformedPayload && (
              <Grid item xs={12} style={{ width: "100%" }}>
                <DataAccordion
                  title="Transformed UPMS Payload"
                  data={transformedPayload}
                />
              </Grid>
            )}
            {upmsResponse && (
              <Grid item xs={12} style={{ width: "100%" }}>
                <div ref={responseRef}>
                  <DataAccordion
                    title="UPMS Ingestion Response"
                    data={upmsResponse}
                  />
                </div>
              </Grid>
            )}
            {upmsResponse && (
              <>
                <ResetWorkflowButton
                  onReset={() => {
                    resetUPMS();
                    resetForm();
                    setValidationError("");
                    setSelectedWorkflow(WorkflowType.NONE);
                    // Scroll to top
                    setTimeout(() => {
                      workflowContentRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }, 100);
                  }}
                />

                <div ref={step2Ref}>
                  <CustomerCreationSection
                    patientDetails={extractedPatientDetails}
                    showAcknowledgment={true}
                    showCriticalPostIngestionWarning={true}
                    stepNumber="4"
                    title="Create Pharmacy Customer (Optional)"
                    subtitle="Create a new pharmacy customer using the prescription data"
                  />
                </div>

                <ResetWorkflowButton
                  onReset={() => {
                    resetUPMS();
                    resetForm();
                    setValidationError("");
                    setSelectedWorkflow(WorkflowType.NONE);
                    // Scroll to top
                    setTimeout(() => {
                      workflowContentRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }, 100);
                  }}
                />
              </>
            )}
          </Grid>
        </div>
      )}

      {isIngesting && <LoadingSpinner />}
    </Grid>
  );
};

export default IngestTransferRxToUPMSSection;
